/**
 * @fileoverview Re-mede banda em background a cada 4h (Bug #2 da
 * sessão 2026-05-08).
 *
 * **Por que existe**: o probe de banda original só rodava no boot/login
 * (junto com o snapshot). Bandwidth class era usada por capabilities
 * derivadas (`playerQuality`, `livePreviewEnabled`, `prefetchConcurrency`,
 * `imagePreset`) e ficava estável durante toda a sessão. Em TV (sessões
 * 8h+), Wi-Fi pode oscilar de manhã pra noite. App não reagia.
 *
 * **Solução**: probe leve dedicado (1MB via `/v1/probe/blob` ou
 * Cloudflare anycast) periódico. ~6MB/dia/user em sessão típica
 * (~3 ticks visíveis × 1MB warmup adjacente). Vs 60MB/dia se usasse
 * default de 10MB do `measureBandwidth`.
 *
 * **Anti-flapping (hysteresis)**: bandwidth class só muda se 2 medições
 * consecutivas concordarem. Inclusive a 1ª medição: se vem com class
 * diferente do `getCachedBandwidth()`, exige confirmação. Sem isso, blip
 * de 1s no Wi-Fi rebaixaria `medium` → `low` → `medium` em 5 min.
 * Capabilities reagiriam, telas re-renderizariam — caro em vlow Tizen.
 *
 * **Skip de refresh quando class igual**: mesmo quando aplica, só chama
 * `refreshNetworkSignal()` se a class mudou de fato. Sem isso, mbps
 * 8.5 → 9.0 (ambos `medium`) re-renderiza telas via `useCapability` à toa.
 *
 * **Guards**:
 * - Skip se rota atual é player (`/player/*`, `/vod-player/*`,
 *   `/series-player/*`, `/catchup/*`) — não roubar banda do streaming.
 * - Skip se há caminho crítico em voo (`isCriticalInFlight()` — sign ou
 *   buffering até o 1º frame), inclusive FORA das rotas de player: o preview
 *   do LiveHero toca vídeo na Home. Ver o comentário no `probe()`.
 * - Skip se `document.visibilityState === 'hidden'` — TV em standby
 *   não precisa medir.
 * - Skip se `!isAuthed` (deslogado, não tem motivo).
 * - Initial probe pulado se `getCachedBandwidth()` mediu há <30min
 *   (re-medir 30s depois seria desperdício de banda).
 *   ⚠️ DÍVIDA CONHECIDA, não corrigida aqui: este skip NUNCA dispara pelo
 *   caminho do boot. O mbps do boot é calculado em `catalog-snapshot.ts`
 *   (bytes/tempo do download do snapshot) e persistido no IDB, mas não passa
 *   por `setLastMeasuredMbps` — e `getCachedBandwidth()` só é populado por
 *   `measureBandwidth()`. Resultado: o probe de 1MB roda 30s depois de todo
 *   boot mesmo assim. Ligar as duas pontas mataria esse probe redundante, MAS
 *   injeta uma medição de fora da máquina de hysteresis (que existe por um
 *   motivo) e tem consequência de capability (player quality, prefetch, preset
 *   de imagem). Fica FORA do fix de ordem de foco no boot, que não mede
 *   capability. Ver o relatório de `fix/boot-foco-overlay-banda`.
 *
 * **Visibility wake-up**: ao voltar de standby (`visibilitychange` →
 *  visible), dispara probe se passou >30min desde a última. Cobre TV
 *  ligada virando o dia com Wi-Fi possivelmente diferente.
 *
 * **Telemetria**: emite `bandwidth_remeasured` (mbpsBefore/After,
 * classBefore/After, applied, result, source).
 */

import {
  type BandwidthClass,
  getCachedBandwidth,
  measureBandwidth,
  refreshNetworkSignal,
  setLastMeasuredMbps,
} from '@app-iptv/device'
import { useEffect, useRef } from 'react'

import { getApiBase } from '../api'
import { isCriticalInFlight } from '../net-priority'
import { track } from '../qoe'
import { useSessionStore } from '../store'

const REFRESH_INTERVAL_MS = 4 * 60 * 60 * 1000 // 4h
const INITIAL_DELAY_MS = 30 * 1000 // 30s — espera bootstrap acalmar
const PROBE_TIMEOUT_MS = 5_000
const PROBE_SIZE_BYTES = 1_048_576 // 1MB — leve mas suficiente pra
// classificar (limites em 2/5/12/30 Mbps; em link 30Mbps mede em
// ~270ms, sobra margem pro 5s timeout). Default do `measureBandwidth`
// (10MB) era pesado pra probe periódico — ver discussão Bug #2.
const SKIP_INITIAL_IF_MEASURED_WITHIN_MS = 30 * 60 * 1000 // 30min
const VISIBILITY_MIN_INTERVAL_MS = 30 * 60 * 1000 // 30min

// Rotas onde NÃO medimos banda (player ativo). Match por prefixo do
// pathname — cobre `/player/:id`, `/vod-player/:id`, etc.
const PLAYER_PATH_PREFIXES = [
  '/player/',
  '/vod-player/',
  '/series-player/',
  '/catchup/',
]

function isPlaybackPath(pathname: string): boolean {
  return PLAYER_PATH_PREFIXES.some((p) => pathname.startsWith(p))
}

interface PendingChange {
  /** Class proposta pela medição anterior (ainda não aplicada). */
  proposed: BandwidthClass
  /** Mbps medido na vez anterior (apenas pra logging). */
  proposedMbps: number
}

/**
 * Hook que orquestra remensuração periódica.
 *
 * Idempotente — se rodar 2× no mesmo mount, segundo é no-op.
 */
export function useBandwidthRefresh(): void {
  const isAuthed = useSessionStore((s) => s.isAuthed)
  // Runtime-config, não build-time: em `file://` (TV) uma base vazia produz
  // `file:///v1/...` → ERR_ACCESS_DENIED. Ver Poster.tsx (raiz do "sem cover").
  const apiBase = getApiBase()

  // Hysteresis: se a medição N propôs mudança de class, espera medição
  // N+1 confirmar antes de aplicar. Persistido entre re-renders via ref.
  const pendingChangeRef = useRef<PendingChange | null>(null)
  // Timestamp da última medição BEM-SUCEDIDA (qualquer source). Usado
  // pelo visibility handler pra evitar refetch logo após poll periódico.
  const lastMeasuredAtRef = useRef<number>(0)

  useEffect(() => {
    if (!isAuthed) return

    let cancelled = false

    const probe = async (): Promise<void> => {
      if (cancelled) return
      // Guards síncronos no momento da execução (não no schedule —
      // user pode estar em playback agora).
      if (document.visibilityState !== 'visible') return
      // window.location é síncrono e sempre atual — não precisa de
      // useLocation (que exigiria RouterContext, indisponível aqui pq
      // o hook roda em AppProviders que envolve o RouterProvider).
      if (isPlaybackPath(window.location.pathname)) return
      // Caminho crítico em voo (sign → buffering até o 1º frame): PULA a
      // rodada. O guard de rota acima não cobre isto — o preview de vídeo do
      // LiveHero toca na Home, e o probe de 30s pós-boot pode cair bem em cima
      // de um play. Baixar 1MB aqui roubaria banda do vídeo que o usuário está
      // esperando E produziria uma medição ERRADA (mede a banda que sobrou da
      // disputa, não a do link). Pular é melhor que adiar: uma medição é uma
      // amostra periódica de 4h, não uma fila — a próxima rodada mede limpo.
      if (isCriticalInFlight()) return

      const before = getCachedBandwidth()
      let result
      try {
        result = await measureBandwidth({
          fallbackUrl: `${apiBase}/v1/probe/blob`,
          timeoutMs: PROBE_TIMEOUT_MS,
          sizeBytes: PROBE_SIZE_BYTES,
        })
      } catch {
        // Falha silenciosa pra hook em si, mas reporta na telemetria
        // pra dashboard medir taxa de erro.
        if (cancelled) return
        track({
          type: 'bandwidth_remeasured',
          ts: Math.floor(Date.now() / 1000),
          mbpsBefore: before?.mbps ?? null,
          mbpsAfter: 0,
          classBefore: before?.class ?? null,
          classAfter: 'medium', // sentinel; result não foi obtido
          applied: false,
          source: 'none',
        })
        return
      }
      if (cancelled) return
      if (result.mbps === null) {
        track({
          type: 'bandwidth_remeasured',
          ts: Math.floor(Date.now() / 1000),
          mbpsBefore: before?.mbps ?? null,
          mbpsAfter: 0,
          classBefore: before?.class ?? null,
          classAfter: result.class,
          applied: false,
          source: result.source,
        })
        return
      }

      lastMeasuredAtRef.current = Date.now()

      // Hysteresis: aplica mudança apenas se 2 medições consecutivas
      // concordarem. Mesma class = aplica imediato.
      // 1ª medição (`before === null`) NÃO aplica direto — exige
      // confirmação na próxima. Cobre cenário do BLOCKER 2: cache fresco
      // pula overlay, hook mede em momento de blip e rebaixa imediato.
      const beforeClass = before?.class ?? null
      const afterClass = result.class
      let applied = false
      const classChanged = beforeClass !== afterClass

      if (!classChanged && beforeClass !== null) {
        // Class igual — só atualiza mbps numérico SEM disparar refresh
        // (capability deriva da class; mbps interno não muda decisões).
        setLastMeasuredMbps(result.mbps)
        applied = true
        pendingChangeRef.current = null
      } else {
        // Class diferente OU primeira medição — exige hysteresis.
        const pending = pendingChangeRef.current
        if (pending && pending.proposed === afterClass) {
          // Confirma mudança — aplica E dispara refresh.
          setLastMeasuredMbps(result.mbps)
          refreshNetworkSignal()
          applied = true
          pendingChangeRef.current = null
        } else {
          // Primeira detecção da mudança — registra e aguarda confirmação.
          pendingChangeRef.current = {
            proposed: afterClass,
            proposedMbps: result.mbps,
          }
        }
      }

      track({
        type: 'bandwidth_remeasured',
        ts: Math.floor(Date.now() / 1000),
        mbpsBefore: before?.mbps ?? null,
        mbpsAfter: result.mbps,
        classBefore: beforeClass,
        classAfter: afterClass,
        applied,
        source: result.source,
      })
    }

    // Initial probe: pula se `measureBandwidth` recente. Hoje isso só acontece
    // via Configurações ("medir agora") ou por um probe anterior deste mesmo
    // hook — o fetch de boot do snapshot NÃO popula `getCachedBandwidth()`.
    // Ver a dívida conhecida no cabeçalho do arquivo.
    const cachedAtBoot = getCachedBandwidth()
    const recentlyMeasured =
      cachedAtBoot &&
      Date.now() - cachedAtBoot.measuredAt < SKIP_INITIAL_IF_MEASURED_WITHIN_MS
    if (recentlyMeasured) {
      lastMeasuredAtRef.current = cachedAtBoot.measuredAt
    }
    const initialTimer = recentlyMeasured
      ? null
      : setTimeout(() => void probe(), INITIAL_DELAY_MS)
    const intervalTimer = setInterval(() => void probe(), REFRESH_INTERVAL_MS)

    // Visibility wake-up: ao voltar de standby, remede se passou >30min.
    // Espelha padrão do `useCatalogRefresh`. Sem isso, TV ligada virando
    // o dia (standby 8h) só remede no próximo tick do interval (até 4h).
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return
      const sinceLast = Date.now() - lastMeasuredAtRef.current
      if (sinceLast < VISIBILITY_MIN_INTERVAL_MS) return
      void probe()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      if (initialTimer) clearTimeout(initialTimer)
      clearInterval(intervalTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isAuthed, apiBase])
}
